import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

import {
  Box,
  createColumnHelper,
  DataTable,
  Text,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";
import { IssueViewContext } from "#app/components/issue-view";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { DashboardActivityDrilldownDocument, type DashboardDrilldownActivity } from "#app/lib/data";
import { formatDateTime } from "#app/lib/utils/format-date";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { AgeReasonGroupSummary } from "./AgeReasonDrilldown";
import { useDrilldownGroups } from "../hooks/use-drilldown-groups";
import {
  DrilldownGroupTitle,
  DrilldownIssueLabel,
  DrilldownMemberTag,
} from "./DrilldownGroupTitle";
import type { Omittable } from "@salestrak/ui";

const GROUP_PAGE_SIZE = 50;

// ── Row type ─────────────────────────────────────────────────────────────────

type ActivityRow = {
  id: string;
  _issueId: string;
  _lastUpdateRaw: string;
  _updatesRaw: number;
  project: string;
  address: string;
  lastUpdate: string;
  updates: string;
  reason: string;
  stage: string;
  workflow: string;
};

const h = createColumnHelper<ActivityRow>();

// ── Group title ──────────────────────────────────────────────────────────────

const DailyGroupTitle = React.memo(function DailyGroupTitle({
  userId,
  name,
  count,
  onVisible,
}: Omittable<{
  userId: string;
  name: string;
  count: number;
  onVisible?: () => void;
}>) {
  return (
    <DrilldownGroupTitle onVisible={onVisible}>
      <Box direction="row" gap="xs" align="center">
        <DrilldownMemberTag userId={userId} name={name} />
        <Text size="xs" variant="muted">
          {count} {count === 1 ? "update" : "updates"}
        </Text>
      </Box>
    </DrilldownGroupTitle>
  );
});

// ── Props ─────────────────────────────────────────────────────────────────────

export type DailyActivityQueryInput = Omittable<{
  startDate: string;
  endDate: string;
  teamIds?: string[];
  memberIds?: string[];
  subjectStartDate: string;
  subjectEndDate?: string;
  timezone: string;
  sortField?: string;
  sortDirection?: string;
}>;

type DailyActivityTableProps = Omittable<{
  summaries: AgeReasonGroupSummary[];
  queryInput: DailyActivityQueryInput;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  scrollToMemberId?: string | null;
  scrollTick?: number;
  pinProjectColumn?: boolean;
}>;

// ── Component ─────────────────────────────────────────────────────────────────

export function DailyActivityTable({
  summaries,
  queryInput,
  sorting,
  onSortingChange,
  scrollToMemberId,
  scrollTick = 0,
  pinProjectColumn = true,
}: DailyActivityTableProps) {
  const issueViewCtx = React.useContext(IssueViewContext);
  const { registerState, getGroupProps } = useGroupStates<DashboardDrilldownActivity>();
  const summaryKeys = useMemo(() => summaries.map((s) => s.userId), [summaries]);
  const { activatedGroupKeys, activateGroup } = useDrilldownGroups(summaryKeys);

  const openIssueFromRow = useCallback(
    (row: ActivityRow) => {
      if (row._issueId)
        issueViewCtx?.openIssueById(row._issueId, { initialTab: "activity" });
    },
    [issueViewCtx],
  );

  useEffect(() => {
    if (!scrollToMemberId || !scrollTick) return;
    activateGroup(scrollToMemberId);
  }, [scrollToMemberId, scrollTick, activateGroup]);

  const sort = sorting[0];


  const getConnection = useCallback(
    (data: any) => data?.dashboardActivityDrilldown,
    [],
  );

  // Collapse multiple activities per issue into one row (same as AgeReasonDrilldown)
  // The drilldown row is a CLIENT-ONLY aggregate, not a server noun: it
  // collapses and re-sorts the page in memory. It is built here, where the
  // row type is declared, never inside the data layer.
  const toRows = useCallback(
    (nodes: readonly DashboardDrilldownActivity[]) => {
      const byIssue = new Map<
        string,
        {
          count: number;
          latest: DashboardDrilldownActivity;
        }
      >();

      for (const activity of nodes) {
        const existing = byIssue.get(activity.issueId);
        if (!existing) {
          byIssue.set(activity.issueId, { count: 1, latest: activity });
          continue;
        }
        existing.count += 1;
        if (activity.creationDate > existing.latest.creationDate) {
          existing.latest = activity;
        }
      }

      const rows: ActivityRow[] = Array.from(
        byIssue.entries(),
      ).map(([issueId, { count, latest }]) => ({
        id: issueId,
        _issueId: issueId,
        _lastUpdateRaw: latest.creationDate,
        _updatesRaw: count,
        project: latest.issueName ?? `#${issueId}`,
        address: latest.issueAddress ?? "—",
        lastUpdate: formatDateTime(latest.creationDate),
        updates: String(count),
        reason: latest.reasonName ?? latest.action ?? "—",
        stage: latest.issueStageName ?? "—",
        workflow: latest.issueWorkflowName ?? "—",
      }));

      if (sort) {
        const { id: sortKey, desc } = sort;
        const dir = desc ? -1 : 1;

        rows.sort((a, b) => {
          let cmp = 0;
          switch (sortKey) {
            case "lastUpdate":
              cmp = a._lastUpdateRaw.localeCompare(b._lastUpdateRaw);
              break;
            case "updates":
              cmp = a._updatesRaw - b._updatesRaw;
              break;
            default:
              cmp = String(
                a[sortKey as keyof ActivityRow] ?? "",
              ).localeCompare(
                String(b[sortKey as keyof ActivityRow] ?? ""),
              );
          }
          return cmp * dir;
        });
      } else {
        rows.sort((a, b) =>
          b._lastUpdateRaw.localeCompare(a._lastUpdateRaw),
        );
      }

      return rows;
    },
    [sort],
  );

  const groups = useMemo<DataTableGroupType<ActivityRow>[]>(() => {
    return summaries.map((summary) => ({
      key: summary.userId,
      label: summary.name,
      totalCount: summary.totalProjects,
      ...getGroupProps(summary.userId),
      data: toRows(getGroupProps(summary.userId).data),
    }));
  }, [summaries, getGroupProps, toRows]);

  const summaryByUserId = useMemo(() => {
    const map = new Map<string, AgeReasonGroupSummary>();
    for (const s of summaries) map.set(s.userId, s);
    return map;
  }, [summaries]);

  const groupTitle = useCallback(
    (groupKey: string) => {
      const summary = summaryByUserId.get(groupKey);
      if (!summary) return null;
      return (
        <DailyGroupTitle
          userId={summary.userId}
          name={summary.name}
          count={summary.totalUpdates}
          onVisible={() => activateGroup(groupKey)}
        />
      );
    },
    [activateGroup, summaryByUserId],
  );

  const tableView = useTableViewSettings("drilldown:dailyActivity");

  const columns = useMemo<DataTableColumnDef<ActivityRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "project",
        header: "Project",
        content: (row) => (
          <DrilldownIssueLabel issueId={row._issueId} name={row.project} />
        ),
        onClickAction: (row) => openIssueFromRow(row),
        actionLabel: (row) => `Open ${row.project}`,
      }),
      h.accessor({ accessorKey: "lastUpdate", header: "Last Update", enableSorting: true, meta: { minWidth: 160 } }),
      h.accessor({ accessorKey: "address", header: "Address", enableSorting: true, meta: { minWidth: 220 } }),
      h.accessor({ accessorKey: "reason", header: "Reason", enableSorting: true, meta: { minWidth: 130 } }),
      h.accessor({ accessorKey: "updates", header: "Age Updates", enableSorting: true, meta: { minWidth: 120 } }),
      h.accessor({ accessorKey: "stage", header: "Stage", enableSorting: true, meta: { minWidth: 120 } }),
      h.accessor({ accessorKey: "workflow", header: "Workflow", enableSorting: true, meta: { minWidth: 120 } }),
    ],
    [openIssueFromRow],
  );

  // Scroll to the group matching scrollToMemberId when it changes
  const tableRef = useRef<any>(null);
  useEffect(() => {
    if (!scrollToMemberId || !scrollTick) return;
    const targetGroup = groups.find((g) => g.key === scrollToMemberId);
    if (!targetGroup) return;

    const container = tableRef.current as HTMLElement | null;
    if (!container) return;

    const scrollEl =
      (container.querySelector('[role="table"]') as HTMLElement) ??
      (container.querySelector(".overflow-auto") as HTMLElement);
    if (!scrollEl) return;

    const savedScroll = scrollEl.scrollTop;
    scrollEl.style.overflow = "hidden";
    scrollEl.scrollTop = 0;
    void scrollEl.offsetHeight;

    const buttons = scrollEl.querySelectorAll('[role="button"]');
    let targetBtn: HTMLElement | null = null;
    for (const btn of Array.from(buttons)) {
      const text = btn.textContent?.trim() ?? "";
      if (text.startsWith(targetGroup.label)) {
        targetBtn = btn as HTMLElement;
        break;
      }
    }

    if (!targetBtn) {
      scrollEl.style.overflow = "";
      scrollEl.scrollTop = savedScroll;
      return;
    }

    const scrollRect = scrollEl.getBoundingClientRect();
    const btnRect = targetBtn.getBoundingClientRect();
    const targetTop = btnRect.top - scrollRect.top;

    scrollEl.style.overflow = "";
    scrollEl.scrollTop = Math.max(0, targetTop - 4);
  }, [scrollToMemberId, scrollTick, groups]);

  return (
    <>
      {summaries
        .filter((summary) => activatedGroupKeys.has(summary.userId))
        .map((summary) => (
          <GroupQueryProvider<DashboardDrilldownActivity>
            key={summary.userId}
            groupKey={summary.userId}
            document={DashboardActivityDrilldownDocument}
            variables={{
              input: {
                ...queryInput,
                subject: "age",
                userId: summary.userId,
              },
              limit: GROUP_PAGE_SIZE,
            }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      <Box direction="column" ref={tableRef} fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          groupTitle={groupTitle}
          getRowId={(r) => r.id}
          density="compact"
          fillColumnId="project"
          pinnedColumnIds={pinProjectColumn ? ["project"] : undefined}
          stickyHeader
          hideGroupCount
          paddingX="md"
          sorting={sorting}
          onSortingChange={onSortingChange}
          {...tableView.tableProps}
        />
      </Box>
    </>
  );
}
